import React from "react";
import { motion } from "framer-motion";
import {
  Flame,
  BarChart3,
  CheckCircle,
  Globe,
  Award,
  Mail,
  Star,
  Target,
  Leaf,
  Gauge,
  FileCheck,
  Building2,
  Zap,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const Carbon_GHG_Management = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a1a] via-[#2d5016] to-[#1f4620] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-lime-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-green-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Flame size={18} className="text-lime-300" />
            <span>Emissions Management</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-lime-300 via-green-300 to-emerald-300">
            Carbon / GHG Management
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Track, Reduce, and Manage Greenhouse Gas Emissions for a Sustainable
            Future
          </p>
        </motion.div>
      </section>

      {/* What is GHG Management */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is Carbon / GHG Emissions Management?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              Carbon and GHG emissions management is a systematic approach to
              measuring, reducing, and reporting greenhouse gases emitted from
              business operations. This process involves tracking emissions,
              setting reduction targets, implementing improvement strategies,
              and reporting in compliance with international standards.
            </p>
            <p>
              Effective GHG management enables businesses to reduce their
              environmental footprint, enhance energy efficiency, and comply
              with national and international environmental regulations while
              working toward climate goals and sustainability targets.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of Carbon / GHG Management
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Carbon Footprint Calculation",
                desc: "Measures total GHG emissions including direct and indirect sources",
                icon: Gauge,
              },
              {
                title: "GHG Inventory",
                desc: "Develops comprehensive inventory of all emissions sources",
                icon: FileCheck,
              },
              {
                title: "Emissions Reduction Targets",
                desc: "Establishes measurable targets aligned with climate goals",
                icon: Target,
              },
              {
                title: "Reporting and Transparency",
                desc: "Complies with GHG Protocol and ISO 14064 standards",
                icon: BarChart3,
              },
              {
                title: "Energy Efficiency Audits",
                desc: "Identifies opportunities to reduce energy consumption",
                icon: Zap,
              },
              {
                title: "Carbon Offsetting",
                desc: "Implements programs to neutralize unavoidable emissions",
                icon: Leaf,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-green-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* GHG Reporting */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is Carbon / GHG Emissions Reporting?
          </h2>
          <div className="bg-gradient-to-r from-green-50 to-lime-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              Carbon and GHG emissions reporting involves transparent disclosure
              of your organization's carbon footprint and emission reduction
              progress. Key components include:
            </p>
            <ul className="space-y-3">
              {[
                "Scope 1: Direct emissions from owned/controlled sources",
                "Scope 2: Indirect emissions from purchased electricity",
                "Scope 3: Other indirect emissions from supply chain",
                "Performance metrics tracking and monitoring",
                "Compliance with GHG Protocol and international standards",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Why GHG Management Matters
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Regulatory Compliance - Meet mandatory reporting requirements",
              "Climate Change Mitigation - Reduce carbon footprint globally",
              "Cost Reduction - Achieve energy efficiency and save costs",
              "Market Competitiveness - Lead as sustainability industry leader",
              "Investor Confidence - Demonstrate ESG commitment to stakeholders",
              "Brand Enhancement - Attract eco-conscious customers and partners",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Benefits of GHG Management
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Regulatory Compliance with climate regulations",
              "Improved Sustainability and environmental responsibility",
              "Energy and Cost Savings through optimization",
              "Enhanced Brand Reputation and public trust",
              "Competitive Advantage in sustainable markets",
              "Operational Efficiency improvements",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-green-100 to-lime-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-green-700 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of GHG Management
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Reduced Carbon Footprint and environmental impact",
              "Cost Savings through energy efficiency",
              "Compliance with climate regulations and standards",
              "Enhanced Corporate Sustainability profile",
              "Improved Operational Efficiency across processes",
              "Risk Mitigation in supply chains",
              "Market Leadership in sustainability efforts",
              "Stakeholder Confidence and trust building",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries Managing GHG Emissions
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Manufacturing and Processing",
              "Energy and Utilities",
              "Transportation and Logistics",
              "Retail and Distribution",
              "Food and Beverage",
              "Technology and Data Centers",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-lime-50 rounded-lg"
              >
                <Building2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Manage */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Manage and Reduce Carbon / GHG Emissions
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Measure Your Carbon Footprint",
                desc: "Conduct comprehensive assessment of GHG emissions across operations",
              },
              {
                step: 2,
                title: "Set Reduction Targets",
                desc: "Establish measurable emissions reduction goals",
              },
              {
                step: 3,
                title: "Identify Emission Sources",
                desc: "Prioritize key emissions sources for reduction efforts",
              },
              {
                step: 4,
                title: "Implement Reduction Strategies",
                desc: "Adopt energy efficiency and renewable energy solutions",
              },
              {
                step: 5,
                title: "Monitor and Report Progress",
                desc: "Track metrics and ensure compliance with reporting standards",
              },
              {
                step: 6,
                title: "Offset Unavoidable Emissions",
                desc: "Invest in carbon offsetting and renewable projects",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-lime-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-50 via-lime-50 to-emerald-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for GHG Management?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions provides expert GHG emissions management consultancy
              services, helping businesses reduce their environmental footprint
              and improve sustainability performance.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Carbon Footprint Calculation",
                "Emissions Reduction Strategy",
                "Energy Efficiency Audits",
                "Carbon Offsetting Solutions",
                "Regulatory Compliance Support",
                "ESG Reporting Assistance",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help businesses achieve carbon neutrality and GHG reduction
              through expert management strategies and sustainable operations.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default Carbon_GHG_Management;
